import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { openNotification } from '../../../../helpers/notification';
import { Empty } from 'antd';
import { getConsultantV2ByUserId } from '../../../../helpers/api';
import { properCase } from '../../../../helpers/utils';
import {
  CalendarTodayOutlined,
  CategoryOutlined,
  DeleteOutline,
  DoNotDisturbAltOutlined,
  EditOutlined,
  EmailOutlined,
  HowToRegOutlined,
  LocalPhoneOutlined,
  PersonOutline,
  TaskAlt,
  ThumbDownAltOutlined,
  VerifiedUserOutlined,
} from '@mui/icons-material';
import ApproveNewConsultant from '../consultants-dialogs/ApproveNewConsultant';
import RejectNewConsultant from '../consultants-dialogs/RejectNewConsultant';
import DeleteConsultant from '../consultants-dialogs/DeleteConsultant';
import DeactivateConsultant from '../consultants-dialogs/DeactiveConsultant';
import EditConsultant from '../consultants-dialogs/EditConsultant';
import { activityStatus, userCats } from '../../../../helpers/appstate';
import ActivateConsultant from '../consultants-dialogs/ActivateConsultant';
import LoaderSkeleton from '../../../common/LoaderSkeleton';

const BRAND = '#0c5bab';

const getStatusColor = (status) => {
  if (status === 'ACTIVE') return '#2e7d32';
  if (status === 'PENDING') return '#ed6c02';
  if (status === 'INACTIVE' || status === 'DEACTIVATED') return '#d32f2f';
  return '#757575';
};

// Labelled action button (EDM only)
const ActionButton = ({ label, onClick, color, icon: IconComponent, variant = 'outlined' }) => (
  <Button
    size="small"
    variant={variant}
    disableElevation
    startIcon={<IconComponent fontSize="small" />}
    onClick={onClick}
    sx={{
      borderRadius: '8px',
      fontWeight: 700,
      flex: '1 1 auto',
      ...(variant === 'contained'
        ? { bgcolor: color, '&:hover': { bgcolor: color, filter: 'brightness(0.92)' } }
        : {
            color,
            borderColor: alpha(color, 0.4),
            '&:hover': { borderColor: color, bgcolor: alpha(color, 0.06) },
          }),
    }}
  >
    {label}
  </Button>
);

const InfoRow = ({ label, value, icon, action }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.25 }}>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '10px',
        display: 'grid',
        placeItems: 'center',
        bgcolor: alpha(BRAND, 0.08),
        color: BRAND,
        flexShrink: 0,
        '& svg': { fontSize: 18 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} noWrap title={value || ''}>
        {value || 'N/A'}
      </Typography>
    </Box>
    {action}
  </Stack>
);

export default function ConsultantDetails({ props, consultantUserId }) {
  const { userCategory } = props;
  const [consultant, setConsultant] = useState();
  const [consultantLoading, setConsultantLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const [dialogApprove, setDialogApprove] = useState(false);
  const [dialogReject, setDialogReject] = useState(false);
  const [dialogDelete, setDialogDelete] = useState(false);
  const [dialogDeactivate, setDialogDeactivate] = useState(false);
  const [dialogUpdate, setDialogUpdate] = useState(false);
  const [dialogActivate, setDialogActivate] = useState(false);

  useEffect(() => {
    fetchConsultantData();
  }, [consultantUserId]); // Refetch when ID changes

  useEffect(() => {
    if (refresh) fetchConsultantData();
  }, [refresh]);

  const fetchConsultantData = async () => {
    setConsultantLoading(true);
    try {
      const res = await getConsultantV2ByUserId(consultantUserId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setConsultant(data);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      const errmsg = error?.response?.data?.result?.desc || 'Error loading profile';
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setConsultantLoading(false);
      setRefresh(false);
    }
  };

  const getInitials = () =>
    [consultant?.firstname, consultant?.lastname]
      .filter(Boolean)
      .map((n) => n.trim()[0]?.toUpperCase())
      .join('');

  const renderActions = () => {
    const status = consultant?.status;
    if (status === activityStatus.PENDING) {
      return (
        <>
          <ActionButton
            label="Approve"
            color="#2e7d32"
            variant="contained"
            icon={HowToRegOutlined}
            onClick={() => setDialogApprove(true)}
          />
          <ActionButton
            label="Reject"
            color="#d32f2f"
            icon={ThumbDownAltOutlined}
            onClick={() => setDialogReject(true)}
          />
        </>
      );
    }
    if (status === activityStatus.ACTIVE) {
      return (
        <>
          <ActionButton
            label="Edit"
            color={BRAND}
            icon={EditOutlined}
            onClick={() => setDialogUpdate(true)}
          />
          <ActionButton
            label="Deactivate"
            color="#ed6c02"
            icon={DoNotDisturbAltOutlined}
            onClick={() => setDialogDeactivate(true)}
          />
          <ActionButton
            label="Delete"
            color="#d32f2f"
            icon={DeleteOutline}
            onClick={() => setDialogDelete(true)}
          />
        </>
      );
    }
    if (status === activityStatus.INACTIVE) {
      return (
        <ActionButton
          label="Activate"
          color="#2e7d32"
          variant="contained"
          icon={TaskAlt}
          onClick={() => setDialogActivate(true)}
        />
      );
    }
    return null;
  };

  const renderContent = () => {
    const statusColor = getStatusColor(consultant?.status);
    const actions = userCategory === userCats.EDM ? renderActions() : null;

    const details = [
      {
        label: 'Email address',
        value: consultant?.email,
        icon: <EmailOutlined />,
        action: consultant?.email ? (
          <Tooltip title="Send an email">
            <IconButton
              size="small"
              component="a"
              href={`mailto:${consultant.email}`}
              sx={{ color: BRAND }}
            >
              <EmailOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
      },
      {
        label: 'Phone number',
        value: consultant?.phone,
        icon: <LocalPhoneOutlined />,
        action: consultant?.phone ? (
          <Tooltip title="Call">
            <IconButton
              size="small"
              component="a"
              href={`tel:${consultant.phone}`}
              sx={{ color: BRAND }}
            >
              <LocalPhoneOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
      },
      {
        label: 'User category',
        value: consultant?.userCategory ? properCase(consultant.userCategory) : '',
        icon: <CategoryOutlined />,
      },
      { label: 'Role', value: consultant?.role, icon: <VerifiedUserOutlined /> },
      {
        label: 'Registered on',
        value: consultant?.entryDate
          ? new Date(consultant.entryDate).toLocaleDateString('en-NG', { dateStyle: 'medium' })
          : '',
        icon: <CalendarTodayOutlined />,
      },
    ];

    return (
      <Box>
        {/* Banner + avatar */}
        <Box
          sx={{ height: 88, background: `linear-gradient(135deg, ${BRAND} 0%, #2f80d1 100%)` }}
        />
        <Box sx={{ px: 3, mt: '-40px', textAlign: 'center' }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mx: 'auto',
              bgcolor: '#fff',
              color: BRAND,
              fontSize: 28,
              fontWeight: 700,
              border: '4px solid #fff',
              boxShadow: `0 6px 18px ${alpha(BRAND, 0.25)}`,
            }}
          >
            {getInitials() || <PersonOutline fontSize="large" />}
          </Avatar>
          <Typography variant="h6" fontWeight={700} sx={{ mt: 1.5, lineHeight: 1.3 }}>
            {consultant?.firstname} {consultant?.lastname}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 1 }}
          >
            <Chip
              label={consultant?.role ? properCase(consultant.role) : 'Consultant'}
              size="small"
              sx={{ fontWeight: 600, bgcolor: alpha(BRAND, 0.08), color: BRAND }}
            />
            {consultant?.status && (
              <Chip
                label={properCase(consultant.status)}
                size="small"
                icon={
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: statusColor,
                      ml: '8px !important',
                    }}
                  />
                }
                sx={{
                  fontWeight: 600,
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                  color: statusColor,
                }}
              />
            )}
          </Stack>
        </Box>

        {/* Actions (EDM only) */}
        {actions && (
          <Box sx={{ px: 3, pt: 2.5 }}>
            {consultant?.status === activityStatus.PENDING && (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mb: 1.5 }}
              >
                This consultant is waiting for approval.
              </Typography>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {actions}
            </Stack>
          </Box>
        )}

        {/* Details */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Consultant details
          </Typography>
          <Box
            sx={{
              '& > *:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
            }}
          >
            {details.map((item) => (
              <InfoRow key={item.label} {...item} />
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {consultantLoading ? (
        <Box sx={{ p: 4 }}>
          <LoaderSkeleton />
          <Divider sx={{ my: 3 }} />
          <LoaderSkeleton />
        </Box>
      ) : !consultant ? (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <Empty description="No consultant data found" />
        </Box>
      ) : (
        renderContent()
      )}

      {/* DIALOGS - Core logic maintained */}
      {dialogApprove && (
        <ApproveNewConsultant
          open={dialogApprove}
          consultant={consultant}
          onClose={() => {
            setDialogApprove(false);
            setRefresh(true);
          }}
        />
      )}
      {dialogReject && (
        <RejectNewConsultant
          open={dialogReject}
          consultant={consultant}
          onClose={() => {
            setDialogReject(false);
            setRefresh(true);
          }}
        />
      )}
      {dialogDelete && (
        <DeleteConsultant
          open={dialogDelete}
          consultant={consultant}
          onClose={() => {
            setDialogDelete(false);
            setRefresh(true);
          }}
        />
      )}
      {dialogDeactivate && (
        <DeactivateConsultant
          open={dialogDeactivate}
          consultant={consultant}
          onClose={() => {
            setDialogDeactivate(false);
            setRefresh(true);
          }}
        />
      )}
      {dialogActivate && (
        <ActivateConsultant
          open={dialogActivate}
          consultant={consultant}
          onClose={() => {
            setDialogActivate(false);
            setRefresh(true);
          }}
        />
      )}
      {dialogUpdate && (
        <EditConsultant
          open={dialogUpdate}
          consultant={consultant}
          onClose={() => {
            setDialogUpdate(false);
            setRefresh(true);
          }}
        />
      )}
    </Box>
  );
}
